import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Input, Panel } from '@gumtree/ui-library';
import { loginThenComeBack } from '@gumtree/shell/src/common/common-actions';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';

import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import { useModal } from '@gumtree/shell/src/use-modal';
import type { VipState, VipUseDispatch } from '../../../reducers';
import { Offer, Price, Root, ToggleContainer } from './make-offer.style';

export default function MakeOffer({ dropdown = false }: Props) {
    const {
        price,
        vatInfo,
        advertId,
        prevOfferSuccess,
        isMobile,
        csrfToken,
        userData,
        isCurrentUserSeller,
        isFromMotorsFeed,
    } = useSelector(staticSelector, () => true);

    const [offer, setOffer] = useState('');
    const [open, setOpen] = useState(false);
    const [formHasError, setFormHasError] = useState(false);
    const [responseSuccess, setResponseSuccess] = useState(null as null | boolean);
    const { openDialog } = useModal();

    useEffect(() => {
        // Initially open
        prevOfferSuccess !== null && setOpen(true);
    }, []);
    const dispatch = useDispatch() as VipUseDispatch;
    const act = useMemo(
        () => ({
            toggle: () => {
                const isUserLoggedIn = !!userData && userData.userLoggedIn;

                if (!isUserLoggedIn) {
                    openDialog(
                        { preventDefault() {} } as React.MouseEvent,
                        LoginModalType.OFFER,
                        true
                    );
                    return;
                }

                if (!isUserLoggedIn) {
                    trackV2('R2SBidBegin');
                    dispatch(loginThenComeBack());
                    return;
                }

                setOpen((x) => {
                    const nextOpen = !x;
                    nextOpen &&
                        trackGA4Event<GA4.ContactSellerOfferEvent>({
                            event: 'contact_seller_offer',
                            contactASellerMethod: 'begin bid',
                            formName: 'make an offer',
                            formValidation: 'success',
                            listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                        });
                    return nextOpen;
                });
            },
            updateOffer: ({ currentTarget: { value } }: FormEvent<HTMLInputElement>) => {
                setOffer(/^[0-9.]*$/.test(value) ? value : offer);
            },
            clearOffer: () => {
                setOffer('');
            },
            sendOffer: async () => {
                const offerPounds = Number(offer);
                if (!offer || !Number.isFinite(offerPounds) || offerPounds < 0) {
                    setFormHasError(true);
                    return;
                }
                setFormHasError(false);

                try {
                    trackV2('R2SBidBegin');
                    await makeOffer(advertId, `${offerPounds}`, csrfToken);
                    trackV2('R2SBidSuccess');
                    trackGA4Event<GA4.ContactSellerOfferEvent>({
                        event: 'contact_seller_offer',
                        contactASellerMethod: 'make bid',
                        formName: 'make an offer',
                        formValidation: 'success',
                        makeOfferAmount: offerPounds,
                        listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                    });
                    setResponseSuccess(true);
                } catch (e) {
                    setResponseSuccess(false);
                    trackGA4Event<GA4.ContactSellerOfferEvent>({
                        event: 'contact_seller_offer',
                        contactASellerMethod: 'make bid',
                        formName: 'make an offer',
                        formValidation: 'failure',
                        makeOfferAmount: offerPounds,
                        listingPartner: isFromMotorsFeed ? 'motors' : undefined,
                    });

                    console.error(e);
                }
            },
            dismissSuccess: () => {
                setResponseSuccess(null);
            },
        }),
        [offer]
    );

    /** Has an offer ever been made? */
    const offerMade = responseSuccess !== null || prevOfferSuccess !== null;
    /** Was the last offer successful? */
    const lastOfferSuccess = offerMade
        ? responseSuccess !== null
            ? responseSuccess
            : prevOfferSuccess
        : null;

    return (
        <>
            <ToggleContainer open={open} dropdown={dropdown} isMobile={isMobile}>
                <Button
                    type="button"
                    display={open ? 'secondary' : 'primary'}
                    fullWidth
                    label={
                        <div>
                            Make offer
                            <Icon type="dropdown" className="toggle" size="smallest" />
                        </div>
                    }
                    data-q="make-offer-button"
                    disabled={Boolean(isCurrentUserSeller)}
                    onClick={act.toggle}
                />
            </ToggleContainer>
            {open && (
                <Root dropdown={dropdown}>
                    <Panel>
                        <form>
                            <fieldset name="provide-offer">
                                <legend>Make the seller an offer for this item</legend>

                                {price && (
                                    <Price>
                                        <div>Price:</div>
                                        <div>
                                            <strong>{price}</strong>
                                            <div className="sub-text">{vatInfo}</div>
                                        </div>
                                    </Price>
                                )}

                                <Offer error={formHasError}>
                                    <div className="offer-label">
                                        <label htmlFor="reply-form-message">Offer:</label>
                                    </div>
                                    <div className="offer-input">
                                        <Input
                                            type="number"
                                            id="reply-form-message"
                                            title="Your offer price"
                                            aria-required
                                            value={offer}
                                            onChange={act.updateOffer}
                                            onClear={act.clearOffer}
                                            onKeyPress={suppressOfferKeys}
                                        />
                                    </div>
                                    <div data-q="make-an-offer-guidance-message">
                                        Please enter a valid offer e.g. 1500.00
                                    </div>
                                    <Button
                                        type="button"
                                        display="primary"
                                        fullWidth
                                        label={<span>Send the offer</span>}
                                        data-q="submit-offer-button"
                                        onClick={act.sendOffer}
                                    />
                                </Offer>
                            </fieldset>

                            {offerMade && (
                                <fieldset name="provide-offer-response">
                                    {lastOfferSuccess ? (
                                        <div>
                                            <b>Success!</b>

                                            <p>Your offer has been sent</p>

                                            <Button
                                                type="button"
                                                display="primary"
                                                fullWidth
                                                label={<span>Close</span>}
                                                onClick={act.dismissSuccess}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <b>Something went wrong!</b>

                                            <p>
                                                We couldn't submit your offer.
                                                <br />
                                                Please try again!
                                            </p>

                                            <Button
                                                type="button"
                                                display="primary"
                                                fullWidth
                                                label={<span>Try again</span>}
                                                onClick={act.sendOffer}
                                            />
                                        </div>
                                    )}
                                </fieldset>
                            )}
                        </form>
                    </Panel>
                </Root>
            )}
        </>
    );
}

interface Props {
    dropdown?: boolean;
}

function staticSelector({ adDetails, baseConfig, sellerActions, userData }: VipState) {
    return {
        advertId: `${adDetails.id}`,
        csrfToken: adDetails.token,
        isMobile: baseConfig.device.type === 'mobile',
        prevOfferSuccess: sellerActions.makeOfferSuccess,
        price: adDetails.price,
        userData,
        vatInfo: adDetails.vatInfo,
        isCurrentUserSeller: adDetails.isCurrentUserSeller,
        isFromMotorsFeed: adDetails.isFromMotorsFeed,
    };
}

async function makeOffer(
    advertId: string,
    offerPounds: string,
    csrfToken: string
): Promise<Response> {
    const params = new URLSearchParams();
    params.append('replyForm.advertId', advertId);
    params.append('replyForm.message', offerPounds);

    const response = await fetch('/make-an-offer/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            // BE identifies AJAX using this header:
            'X-Requested-With': 'XMLHttpRequest',
            X_CSRF_TOKEN: csrfToken,
        },
        body: params.toString(),
    });
    if (!response.ok) {
        throw Error(`/make-an-offer/ failed with status ${response.status}`);
    }
    return response;
}

function suppressOfferKeys(e: React.KeyboardEvent) {
    if (['Enter', 'e', 'E', '-', '+'].includes(e.key)) {
        e.preventDefault();
    }
}
